import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { StepState, ValueType } from 'src/app/core/enums/enums';
import { IClientSettings } from 'src/app/core/models/agency-settings.model';
import { InvoluntaryExchangeQuote } from 'src/app/core/models/involuntary-change.model';
import { Passenger } from 'src/app/core/models/itinerary.model';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { VoluntaryChangeService } from 'src/app/voluntary-change/services/voluntary-change.service';
import {
  IEditValuesModal,
  PenaltyOverride
} from 'src/app/core/models/cancellation-overrides.model';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  FeedbackModelML,
  PenaltyDetail,
  PenaltyFareBasisMachineLearning,
  PenaltiesByTypeUser,
  Price
} from 'src/app/core/models/cancellation.model';

@Component({
  selector: 'app-edit-penalties',
  templateUrl: './edit-penalties.component.html',
  styleUrls: ['./edit-penalties.component.scss']
})
export class EditPenaltiesComponent implements OnInit, OnChanges {
  @Input() showFeedback: boolean = false;
  @Input() readOnly = false;
  @Input() showDetails = false;
  @Input() currencies = ['USD', 'EUR'];
  @Input() passenger: Passenger[];
  @Input() loading = false;
  @Input() set additionalPenalties(values: PenaltyDetail[]) {
    this.penaltiesSegment = values;
  }
  @Input() set penalties(values: PenaltyDetail[]) {
    this.newPenalties = values
      ? values.map(
          (penalty) =>
            ({
              type: ValueType.Fixed,
              value: penalty?.amount?.amount,
              currency: penalty?.amount?.currency,
              passengerType: penalty?.passengerType
            } as PenaltyOverride)
        )
      : [];
  }
  @Input() set feeTax(value: Price) {
    this.newFeeTax = value;
  }
  @Input() set serviceFee(value: Price) {
    this.newServiceFee = value;
  }
  @Input() set state(value: StepState) {
    this.edited = value === StepState.Completed ? true : false;
  }
  @Input() set close(value: boolean) {
    this.open = value;
  }

  @Output() savePenalties = new EventEmitter<IEditValuesModal>();

  public activeContent: number = 0;
  public involuntaryQuote$: Observable<InvoluntaryExchangeQuote>;
  public penaltiesByTypeUser: PenaltiesByTypeUser[] = [];

  open = false;
  disabled = true;
  edited = false;
  newFeeTax: Price;
  newServiceFee: Price;
  passengerTypeForClass: string;
  feedBackRating: boolean;
  needTextReview = false;
  textFeedbackReview = '';
  newPenalties: PenaltyOverride[] = [];
  feeTaxOverride: Price = {
    currency: '',
    amount: 0
  };
  serviceFeeOverride: Price = {
    currency: '',
    amount: 0
  };

  machineLearningModel$: Observable<IClientSettings>;
  MlhasError$: Observable<boolean>;
  fareBasisMachineLearning$: Observable<PenaltyFareBasisMachineLearning>;
  isAirline$: Observable<boolean>;

  constructor(private service: VoluntaryChangeService, private settingsService: SettingsService) {}

  ngOnInit(): void {
    this.machineLearningModel$ = this.settingsService.getClientSettings();
    this.fareBasisMachineLearning$ = this.service.getPenaltiesMLFarebasis();
    this.MlhasError$ = this.service.validateMachineLearningModel();
    this.isAirline$ = this.settingsService.getClientIsAirline();

    this.validatePenalties();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes?.feeTax) {
        this.newFeeTax = changes.feeTax.currentValue;
        this.feeTaxOverride = changes.feeTax.currentValue;
      }

      if (changes?.serviceFee) {
        this.newServiceFee = changes.serviceFee.currentValue;
        this.serviceFeeOverride = changes.serviceFee.currentValue;
      }

      if (changes.additionalPenalties) {
        const penalties: PenaltiesByTypeUser[] = this.passenger
          ?.map((value) => value.type)
          .filter((value, index, self) => self.indexOf(value) === index)
          .map((resp) => ({
            name: resp,
            passengerType: resp,
            farebasis: '',
            listFareBasis: []
          }));

        if (this.penaltiesSegment && this.penaltiesSegment.length > 0) {
          this.penaltiesSegment.forEach((resp: any, key: number) => {
            resp.passengerTypes.forEach((element: string) => {
              penalties?.forEach(
                (passenger, key) =>
                  passenger.name == element && penalties[key].listFareBasis.push(resp)
              );
            });
          });
        }
        this.penaltiesByTypeUser = penalties?.map((element) => ({
          ...element,
          farebasis: element.listFareBasis[0] ? element.listFareBasis[0].fareBasis : ''
        }));
      }
    }
  }

  changeContent(active: number) {
    this.activeContent = active;
  }

  penaltiesSegment: PenaltyDetail[];

  validatePenalties() {
    this.disabled = !this.newPenalties?.reduce(
      (final, penalty) => !!penalty?.value?.toString()?.length && penalty?.value >= 0 && final,
      true
    );
  }

  sendValues() {
    const editValuesVoluntaryModal: IEditValuesModal = {
      penaltiesOverride: this.newPenalties,
      feeTaxOverride: this.feeTaxOverride,
      serviceFeeOverride: this.serviceFeeOverride
    };

    this.savePenalties.emit(editValuesVoluntaryModal);
  }

  validateDynamicClasses(passengerType: string): void {
    this.passengerTypeForClass = passengerType;
  }

  getReliabilityAverage(MLModel: PenaltyFareBasisMachineLearning) {
    return MLModel !== null ? MLModel.totalAverage.toFixed(0) : '--';
  }

  sendFeedBack(review: boolean): void {
    this.feedBackRating = review;
    review ? (this.needTextReview = false) : (this.needTextReview = true);
  }

  generateFeedback() {
    let feedback = {
      agentRating: this.feedBackRating
    } as FeedbackModelML;
    this.service
      .getPenaltiesMLFarebasis()
      .pipe(take(1))
      .subscribe((result) => {
        feedback.id = result.machineLearningLogId;
      });
    !this.feedBackRating && (feedback.agentReview = this.textFeedbackReview);
    this.service.dispatchSendFeedbackMLequest(feedback);
  }
}
